import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { api } from '../../services/api';
import QRCodeModal from '../../components/ui/QRCodeModal';
import { useAuthStore } from '../../store/authStore';

// --- Types ---

type PropertyType = 'house' | 'apartment' | 'condo' | 'lot' | 'parcel' | 'office' | 'warehouse' | 'other';
type OperationType = 'sale' | 'rent' | 'both';
type PropertyStatus = 'draft' | 'active' | 'paused' | 'rented' | 'sold' | 'archived';

interface Property {
  id: number;
  title: string;
  slug: string;
  property_type: PropertyType;
  operation_type: OperationType;
  status: PropertyStatus;
  price: number | null;
  currency: string;
  city: string | null;
  created_at: string;
}

interface CompanyInfo {
  slug: string;
  name: string;
}

interface PropertyListResponse {
  data: Property[];
  meta: {
    total: number;
    page: number;
    page_size: number;
    pages: number;
  };
}

// --- Constants ---

const PROPERTY_TYPES: PropertyType[] = ['house', 'apartment', 'condo', 'lot', 'parcel', 'office', 'warehouse', 'other'];
const OPERATION_TYPES: OperationType[] = ['sale', 'rent', 'both'];
const STATUSES: PropertyStatus[] = ['draft', 'active', 'paused', 'rented', 'sold', 'archived'];

const STATUS_BADGE_CLASS: Record<PropertyStatus, string> = {
  draft: 'badge-ghost',
  active: 'badge-success',
  paused: 'badge-warning',
  rented: 'badge-info',
  sold: 'badge-secondary',
  archived: 'badge-error',
};

const OPERATION_LABEL_SHORT: Record<OperationType, string> = {
  sale: 'sale',
  rent: 'rent',
  both: 'both_short',
};

// --- Component ---

export default function PropertyListPage(): React.ReactElement {
  const { t } = useTranslation('properties');
  const { t: tCommon } = useTranslation('common');
  const queryClient = useQueryClient();

  // Filters
  const [filterType, setFilterType] = useState<string>('');
  const [filterOperation, setFilterOperation] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  // Pagination
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });

  // Status change dialog
  const [statusDialog, setStatusDialog] = useState<{
    open: boolean;
    property: Property | null;
    newStatus: PropertyStatus | null;
  }>({ open: false, property: null, newStatus: null });

  // Delete confirmation dialog
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    property: Property | null;
  }>({ open: false, property: null });

  // QR share
  const [qrModal, setQrModal] = useState<{ open: boolean; url: string; title: string }>({ open: false, url: '', title: '' });
  const user = useAuthStore((s) => s.user);

  // Fetch company slug for building public URLs
  const { data: companyData } = useQuery({
    queryKey: ['my-company-info'],
    queryFn: () => api.get<{ data: CompanyInfo }>('/v1/companies/me'),
    enabled: user?.role === 'company_admin' || user?.role === 'agent',
  });
  const companySlug = companyData?.data?.slug;

  // Snackbar
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const statusModalRef = useRef<HTMLDialogElement>(null);
  const deleteModalRef = useRef<HTMLDialogElement>(null);

  // --- Auto-dismiss snackbar ---
  useEffect(() => {
    if (snackbar.open) {
      const timer = setTimeout(() => setSnackbar((s) => ({ ...s, open: false })), 3000);
      return () => clearTimeout(timer);
    }
  }, [snackbar.open]);

  // --- Sync modals ---
  useEffect(() => {
    if (statusDialog.open) statusModalRef.current?.showModal();
    else statusModalRef.current?.close();
  }, [statusDialog.open]);

  useEffect(() => {
    if (deleteDialog.open) deleteModalRef.current?.showModal();
    else deleteModalRef.current?.close();
  }, [deleteDialog.open]);

  // Build query params
  const queryParams: Record<string, string | number> = {
    page: paginationModel.page + 1,
    page_size: paginationModel.pageSize,
  };
  if (filterType) queryParams.type = filterType;
  if (filterOperation) queryParams.operation = filterOperation;
  if (filterStatus) queryParams.status = filterStatus;

  // Fetch properties
  const { data: response, isLoading } = useQuery({
    queryKey: ['properties', queryParams],
    queryFn: () => api.get<PropertyListResponse>('/v1/properties', queryParams),
  });

  const rows = response?.data ?? [];
  const totalRows = response?.meta?.total ?? 0;
  const totalPages = Math.ceil(totalRows / paginationModel.pageSize);

  // Status change mutation
  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) =>
      api.patch(`/v1/properties/${id}/status`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      setStatusDialog({ open: false, property: null, newStatus: null });
      setSnackbar({ open: true, message: t('list.statusDialog.success'), severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: t('list.statusDialog.error'), severity: 'error' });
    },
  });

  // Delete (soft-delete) mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/v1/properties/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      setDeleteDialog({ open: false, property: null });
      setSnackbar({ open: true, message: t('list.deleteDialog.success'), severity: 'success' });
    },
    onError: () => {
      setSnackbar({ open: true, message: t('list.deleteDialog.error'), severity: 'error' });
    },
  });

  const handleStatusChangeRequest = (property: Property, newStatus: PropertyStatus): void => {
    setStatusDialog({ open: true, property, newStatus });
  };

  const handleStatusChangeConfirm = (): void => {
    if (statusDialog.property && statusDialog.newStatus) {
      statusMutation.mutate({
        id: statusDialog.property.id,
        status: statusDialog.newStatus,
      });
    }
  };

  const handleDeleteRequest = (property: Property): void => {
    setDeleteDialog({ open: true, property });
  };

  const handleDeleteConfirm = (): void => {
    if (deleteDialog.property) {
      deleteMutation.mutate(deleteDialog.property.id);
    }
  };

  const formatPrice = (price: number | null, currency: string): string => {
    if (price == null) return '-';
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: currency || 'ARS',
      maximumFractionDigits: 0,
    }).format(price);
  };

  return (
    <div>
      {/* Header */}
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <h1 className="text-3xl font-bold">{t('list.title')}</h1>
        <div className="flex gap-2">
          {companySlug && (
            <>
              <RouterLink
                to={`/c/${companySlug}`}
                target="_blank"
                className="btn btn-outline btn-sm"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" /></svg>
                Ver catalogo
              </RouterLink>
              <button
                className="btn btn-outline btn-sm"
                onClick={() => setQrModal({ open: true, url: `${window.location.origin}/c/${companySlug}`, title: 'Catalogo de propiedades' })}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h2M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" /></svg>
                QR Catalogo
              </button>
            </>
          )}
          <RouterLink to="/app/properties/new" className="btn btn-primary btn-sm">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
            </svg>
            {t('list.newProperty')}
          </RouterLink>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-6">
        <select
          className="select select-bordered select-sm w-48"
          value={filterType}
          onChange={(e) => {
            setFilterType(e.target.value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        >
          <option value="">{t('list.filters.propertyType')} - {t('list.filters.all')}</option>
          {PROPERTY_TYPES.map((type) => (
            <option key={type} value={type}>
              {t(`propertyType.${type}`)}
            </option>
          ))}
        </select>

        <select
          className="select select-bordered select-sm w-48"
          value={filterOperation}
          onChange={(e) => {
            setFilterOperation(e.target.value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        >
          <option value="">{t('list.filters.operationType')} - {t('list.filters.all')}</option>
          {OPERATION_TYPES.map((op) => (
            <option key={op} value={op}>
              {t(`operationType.${op}`)}
            </option>
          ))}
        </select>

        <select
          className="select select-bordered select-sm w-48"
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPaginationModel((prev) => ({ ...prev, page: 0 }));
          }}
        >
          <option value="">{t('list.filters.status')} - {t('list.filters.all')}</option>
          {STATUSES.map((status) => (
            <option key={status} value={status}>
              {t(`status.${status}`)}
            </option>
          ))}
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-base-100 rounded-lg">
        {isLoading ? (
          <div className="flex justify-center p-8">
            <span className="loading loading-spinner loading-lg" />
          </div>
        ) : (
          <table className="table table-zebra">
            <thead>
              <tr>
                <th>{t('list.columns.title')}</th>
                <th>{t('list.columns.propertyType')}</th>
                <th>{t('list.columns.operationType')}</th>
                <th>{t('list.columns.status')}</th>
                <th>{t('list.columns.price')}</th>
                <th>{t('list.columns.city')}</th>
                <th>{t('list.columns.createdAt')}</th>
                <th>{t('list.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={8} className="text-center text-base-content/60 py-8">
                    {t('list.empty')}
                  </td>
                </tr>
              ) : (
                rows.map((property) => (
                  <tr key={property.id}>
                    <td className="font-medium max-w-xs truncate">{property.title}</td>
                    <td>
                      <span className="badge badge-outline badge-sm whitespace-nowrap">
                        {t(`propertyType.${property.property_type}`)}
                      </span>
                    </td>
                    <td>
                      <span className="badge badge-outline badge-primary badge-sm whitespace-nowrap">
                        {t(`operationType.${OPERATION_LABEL_SHORT[property.operation_type]}`, {
                          defaultValue: t(`operationType.${property.operation_type}`),
                        })}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-sm whitespace-nowrap ${STATUS_BADGE_CLASS[property.status]}`}>
                        {t(`status.${property.status}`)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">{formatPrice(property.price, property.currency)}</td>
                    <td>{property.city || '-'}</td>
                    <td className="whitespace-nowrap">
                      {property.created_at ? format(new Date(property.created_at), 'dd/MM/yyyy') : '-'}
                    </td>
                    <td>
                      <div className="flex items-center gap-1">
                        {/* Share QR button */}
                        {companySlug && property.slug && property.status === 'active' && (
                          <button
                            className="btn btn-ghost btn-xs"
                            title="Compartir QR"
                            onClick={() => setQrModal({
                              open: true,
                              url: `${window.location.origin}/c/${companySlug}/${property.slug}`,
                              title: property.title,
                            })}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" /></svg>
                          </button>
                        )}

                        {/* Edit button */}
                        <RouterLink
                          to={`/app/properties/${property.id}/edit`}
                          className="btn btn-ghost btn-xs"
                          title={t('list.actions.edit')}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                          </svg>
                        </RouterLink>

                        {/* Create contract button */}
                        {['active', 'rented'].includes(property.status) && (
                          <RouterLink
                            to={`/app/contracts/new?property_id=${property.id}`}
                            className="btn btn-ghost btn-xs"
                            title="Crear contrato"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                          </RouterLink>
                        )}

                        {/* Status change dropdown */}
                        <div className="dropdown dropdown-end">
                          <label tabIndex={0} className="btn btn-ghost btn-xs" title={t('list.actions.changeStatus')}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                            </svg>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </label>
                          <ul tabIndex={0} className="dropdown-content z-[1] menu menu-sm p-2 shadow bg-base-100 rounded-box w-40">
                            {STATUSES.filter((s) => s !== property.status).map((status) => (
                              <li key={status}>
                                <button onClick={() => handleStatusChangeRequest(property, status)}>
                                  <span className={`badge badge-xs ${STATUS_BADGE_CLASS[status]}`} />
                                  {t(`status.${status}`)}
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>

                        {/* Delete button */}
                        <button
                          className="btn btn-ghost btn-xs text-error"
                          title={t('list.actions.delete')}
                          onClick={() => handleDeleteRequest(property)}
                        >
                          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalRows > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-base-200">
            <span className="text-sm text-base-content/60">
              {t('list.page', {
                page: paginationModel.page + 1,
                total: totalPages,
              })}
            </span>
            <div className="join">
              <button
                className="join-item btn btn-sm"
                disabled={paginationModel.page === 0}
                onClick={() => setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }))}
              >
                &laquo;
              </button>
              <button className="join-item btn btn-sm">
                {paginationModel.page + 1}
              </button>
              <button
                className="join-item btn btn-sm"
                disabled={paginationModel.page >= totalPages - 1}
                onClick={() => setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }))}
              >
                &raquo;
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Status change confirmation modal */}
      <dialog ref={statusModalRef} className="modal" onClose={() => setStatusDialog({ open: false, property: null, newStatus: null })}>
        <div className="modal-box">
          <h3 className="font-bold text-lg">{t('list.statusDialog.title')}</h3>
          <p className="py-4 text-base-content/70">
            {statusDialog.property &&
              statusDialog.newStatus &&
              t('list.statusDialog.message', {
                title: statusDialog.property.title,
                status: t(`status.${statusDialog.newStatus}`),
              })}
          </p>
          <div className="modal-action">
            <button className="btn" onClick={() => setStatusDialog({ open: false, property: null, newStatus: null })}>
              {tCommon('buttons.cancel')}
            </button>
            <button
              className="btn btn-primary"
              onClick={handleStatusChangeConfirm}
              disabled={statusMutation.isPending}
            >
              {statusMutation.isPending ? <span className="loading loading-spinner loading-sm" /> : tCommon('buttons.confirm')}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setStatusDialog({ open: false, property: null, newStatus: null })}>close</button>
        </form>
      </dialog>

      {/* Delete confirmation modal */}
      <dialog ref={deleteModalRef} className="modal" onClose={() => setDeleteDialog({ open: false, property: null })}>
        <div className="modal-box">
          <h3 className="font-bold text-lg">{t('list.deleteDialog.title')}</h3>
          <p className="py-4 text-base-content/70">
            {deleteDialog.property &&
              t('list.deleteDialog.message', {
                title: deleteDialog.property.title,
              })}
          </p>
          <div className="modal-action">
            <button className="btn" onClick={() => setDeleteDialog({ open: false, property: null })}>
              {tCommon('buttons.cancel')}
            </button>
            <button
              className="btn btn-error"
              onClick={handleDeleteConfirm}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? <span className="loading loading-spinner loading-sm" /> : tCommon('buttons.delete')}
            </button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => setDeleteDialog({ open: false, property: null })}>close</button>
        </form>
      </dialog>

      {/* Toast / Snackbar */}
      {snackbar.open && (
        <div className="toast toast-end toast-bottom z-50">
          <div className={`alert ${snackbar.severity === 'success' ? 'alert-success' : 'alert-error'}`}>
            <span>{snackbar.message}</span>
            <button className="btn btn-ghost btn-xs" onClick={() => setSnackbar((s) => ({ ...s, open: false }))}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* QR Share Modal */}
      <QRCodeModal
        url={qrModal.url}
        title={qrModal.title}
        isOpen={qrModal.open}
        onClose={() => setQrModal({ open: false, url: '', title: '' })}
      />
    </div>
  );
}
